import {clsx} from 'clsx'
import styles from './grid-cells/ActionBar.module.css'
import {ActionList, ActionMenu, Button, ButtonGroup} from '@primer/react'
import type {DiffAnnotation, Thread, ViewerData} from '../conversation-types'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import CommentIndicator from './grid-cells/CommentIndicator'
import {useCallback, useRef, type MouseEvent} from 'react'
import {CellContextMenu} from './grid-cells/CellContextMenu'
import {TriangleDownIcon} from '@primer/octicons-react'
import type {HunkData} from '../helpers/hunk-data-helpers'
import type {DiffAnchor} from '@github-ui/diffs/types'
import type {ClientDiffLine, FileDiffReference} from '../types'
import {copyCode} from '../helpers/copy-code'
import {useGetDiffLinesInfo, useGetSelectedDiffLines, useGetSelectedLineRange} from '../stores/selectors'
/**
 * The ActionBar is a set of buttons that appears on hover or focus of a cell in the diff grid.
 * The buttons will be absolutely positioned to the right side of the diff cell.
 */
type SimpleContextMenuProps = {
  annotations: DiffAnnotation[]
  copilotChatReference?: FileDiffReference
  currentHunk: HunkData | undefined
  diffAnchor: DiffAnchor
  diffLine: ClientDiffLine
  isLeftSide: boolean
  isSplit: boolean
  nextHunk: HunkData | undefined
  previousHunk: HunkData | undefined
  shouldDisplayCollapseComments?: boolean
  shouldDisplayMinimizedComments?: boolean
  showStartConversation: boolean
  threads: Thread[]
  toggleViewingMarkers: () => void
  viewerData: ViewerData
  openStartConversationDialog: () => void
  setShouldStartNewConversationWithSuggestedChange: () => void
  setIsContextMenuOpen: (value: boolean) => void
  contextMenuOpen: boolean
}
export function SimpleContextMenu({
  showStartConversation,
  viewerData,
  openStartConversationDialog,
  threads,
  annotations,
  shouldDisplayMinimizedComments,
  diffAnchor,
  diffLine,
  isLeftSide,
  isSplit,
  nextHunk,
  previousHunk,
  currentHunk,
  copilotChatReference,
  shouldDisplayCollapseComments,
  toggleViewingMarkers,
  setShouldStartNewConversationWithSuggestedChange,
  setIsContextMenuOpen,
  contextMenuOpen,
}: SimpleContextMenuProps) {
  const contextMenuRef = useRef<HTMLButtonElement>(null)
  const totalCommentsCount = threads.reduce((acc, thread) => acc + (thread.commentsData?.comments.length ?? 0), 0)
  const totalAnnotationsCount = annotations.length
  const totalCommentsAndAnnotationsCount = totalCommentsCount + totalAnnotationsCount
  const {commentsPreference, lineSpacingPreference} = viewerData
  const getDiffLinesInfo = useGetDiffLinesInfo()
  const getSelectedDiffLines = useGetSelectedDiffLines()
  const getSelectedLineRange = useGetSelectedLineRange()

  const handleCopyCode = async () => {
    const diffLines = getDiffLinesInfo()
    await copyCode({
      diffLine,
      diffLines,
      getSelectedDiffLines,
      getSelectedLineRange,
      isSplit,
      isLeftSide,
    })
  }

  const hasThreads = totalCommentsAndAnnotationsCount > 0
  const viewConversations = hasThreads && commentsPreference === CommentsPreference.Collapsed
  const hasLeadingButton = viewConversations && shouldDisplayMinimizedComments

  const handleOnClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // if there's a single thread or annotation, prevent default so we don't select the cell.
      // we'll let the conversation selection logic handle selecting the cell(s) associated to the comment.
      if (threads.length === 1 || annotations.length === 1) {
        event.preventDefault()
      }

      toggleViewingMarkers?.()
    },
    [annotations.length, threads.length, toggleViewingMarkers],
  )
  return (
    <>
      <div aria-hidden={false} className={clsx('d-flex', 'flex-row', styles['action-bar-position'])}>
        <ButtonGroup
          className={clsx(
            styles['action-bar-button-group'],
            !hasLeadingButton && styles['action-bar-button-group--no-leading'],
          )}
        >
          {hasLeadingButton && (
            <Button
              aria-expanded={contextMenuOpen}
              aria-label="View comments"
              size="small"
              className={clsx('py-0', 'px-1', styles.actionBarHeight)}
              onClick={handleOnClick}
            >
              <CommentIndicator
                threads={threads}
                annotations={annotations}
                lineSpacingPreference={lineSpacingPreference}
              />
            </Button>
          )}

          <ActionMenu
            anchorRef={contextMenuRef}
            open={contextMenuOpen}
            onOpenChange={() => setIsContextMenuOpen(!contextMenuOpen)}
          >
            <ActionMenu.Anchor>
              <Button
                ref={contextMenuRef}
                aria-haspopup="true"
                aria-label="More actions"
                icon={TriangleDownIcon}
                size="small"
                className={clsx('fgColor-muted', styles.actionBarArrowSizing)}
                onClick={() => setIsContextMenuOpen(!contextMenuOpen)}
              />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay width="medium">
              <ActionList>
                <CellContextMenu
                  threads={threads}
                  annotations={annotations}
                  currentHunk={currentHunk}
                  diffAnchor={diffAnchor}
                  diffLine={diffLine}
                  shouldDisplayCollapseComments={shouldDisplayCollapseComments}
                  copilotChatReference={copilotChatReference}
                  showStartConversation={showStartConversation}
                  handleCopyCode={handleCopyCode}
                  handleViewMarkersSelection={toggleViewingMarkers}
                  isLeftSide={isLeftSide}
                  isSplit={isSplit}
                  nextHunk={nextHunk}
                  previousHunk={previousHunk}
                  startConversationCurrentLine={openStartConversationDialog}
                  startConversationWithSuggestedChange={setShouldStartNewConversationWithSuggestedChange}
                  viewerData={viewerData}
                />
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </ButtonGroup>
      </div>
    </>
  )
}
